import { action, observable } from 'mobx';
import stores from '../../../models/Stores';
import {
  GraphicsLayer,
  esriProjection,
  Point,
  SimpleMarkerSymbol,
  Graphic,
  FeatureLayer,
  SimpleRenderer,
  esriGeometryEngine,
} from 'cd-eft/utils/esriTypes';
import {
  PendingEditManagerVM,
  IPendingEditManagerVMConstructorProps,
} from 'cd-eft/components/common/PendingEditManager/PendingEditManagerVM';
import { DrawingToolsetViewModel } from 'cd-eft/components/common/DrawingTools/viewModel/DrawingToolsetViewModel';
import { SurfaceDef } from '../BreaklineTools/model/SurfaceDef';
import { AddBreaklineTool } from '../BreaklineTools/viewModel/AddBreaklineTool';
import { DeleteBreaklineTool } from '../BreaklineTools/viewModel/DeleteBreaklineTool';
import { DeleteBorderPointTool } from '../BreaklineTools/viewModel/DeleteBorderPointTool';
import { InsertBorderPointsTool } from '../BreaklineTools/viewModel/InsertBorderPointsTool';
import { MoveBreaklineTool } from '../BreaklineTools/viewModel/MoveBreaklineTool';
import { AddPointBreaklineTool } from '../BreaklineTools/viewModel/AddPointBreaklineTool';
import { DeletePointBreaklineTool } from '../BreaklineTools/viewModel/DeletePointBreaklineTool';
import { ExtendBreaklineTool } from '../BreaklineTools/viewModel/ExtendBreaklineTool';
import TextSymbol from '@arcgis/core/symbols/TextSymbol';
import { DesignContainerVM } from '../../Design/DesignContainer/DesignContainerVM';
import { appConfig } from 'cd-eft/config/AppConfig';
import { AppConstants } from 'cd-eft/appConstants';
import LabelClass from '@arcgis/core/layers/support/LabelClass';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { DesignElementController, SurfaceController, StructureDrawingsController, WaterwayDesignToolsReachJsonController, WaterwayDesignToolsController, TerraceReachController, UgoReachController } from 'cd-eft/services/eftSetApi';
import Layer from '@arcgis/core/layers/Layer';
import { SaveReachEditsRequestDTO, WaterwayVertexParamDTO, SaveTerraceReachEditsDTO, SaveTerraceReachEditsResponseDTO, TerraceVertexParamsDTO, SaveUgoReachEditsRequestDTO, UgoVertexParamsDTO } from "cd-eft/_codegen_/openapi-generator/eft-set";
import { saveScreenshot } from 'cd-eft/utils/gisUtils';
import { EditTinablePointsTool } from '../BreaklineTools/viewModel/EditTinablePointsTool';
import Multipoint from '@arcgis/core/geometry/Multipoint';
import { GroundPoint } from '../BreaklineTools/model/GroundPoint';
import { XyzSurvey } from '../BreaklineTools/model/XyzSurvey';
import { TerraceUiState } from 'cd-eft/models/Stores/TerraceDesignStore';
import { MeasureResultsModel } from 'cd-eft/components/common/MeasureTools/MeasureResultsModel';
const gisModel = stores.AppStore.surveyEngineeringStore.gisModel;
const setStore = stores.AppStore.surveyEngineeringStore;
const { designStore, projectsLibraryStore, waterwayDesignStore, terraceDesignStore, ugoDesignStore } = stores.AppStore;

const pointLabelFields: any = [
  {
    name: 'ObjectID',
    type: 'oid',
  },
  {
    name: 'name',
    type: 'string',
  },
  {
    name: 'y',
    type: 'double',
  },
  {
    name: 'x',
    type: 'double',
  },
  {
    name: 'z',
    type: 'double',
  },
  {
    name: 'description',
    type: 'string',
  },
];

// labelling settings
const pointLabelClass: LabelClass = new LabelClass({
  symbol: {
    type: 'text',
    color: [71, 71, 71, 0.7], //'gray',
    font: {
      family: 'Ubuntu Mono',
      size: 11,
    },
  },
  labelPlacement: 'above-right',
  labelExpressionInfo: {
    expression: '$feature.name',
  },
  maxScale: 0,
  minScale: 2500,
  deconflictionStrategy: 'static', //'none',
});

export class SurfaceDesignerVm extends PendingEditManagerVM {
  @observable error: boolean = false;
  @observable errorMsg: string = '';
  @observable isLoading: boolean = false;

  @observable private _surfaceDef: SurfaceDef | undefined;
  @observable addBreaklineTool?: AddBreaklineTool;
  @observable deleteBreaklineTool?: DeleteBreaklineTool;
  @observable addBorderTool?: InsertBorderPointsTool;
  @observable deleteBorderTool?: DeleteBorderPointTool;
  @observable editTinableTool?: EditTinablePointsTool;
  @observable moveBreaklineTool?: MoveBreaklineTool;
  @observable addPointBreaklineTool?: AddPointBreaklineTool;
  @observable deletePointBreaklineTool?: DeletePointBreaklineTool;
  @observable extendBreaklineTool?: ExtendBreaklineTool;

  @observable viewPointerMoveHandle;
  @observable viewPointerDownHandle;
  private viewPointerMoveHandleOn: boolean = false;
  private wdtGroupCount: number = 0; // Counts how many consecutive wdtGroups belong to a single user edit
  private terraceGroupCount: number = 0;
  private ugoGroupCount: number = 0;
  private feedbackLayer = new GraphicsLayer({ title: 'pointsFeedbackLayer', listMode: 'hide' });
  private feedbackSelectedLayer = new GraphicsLayer({ title: 'selectedFeedbackLayer', listMode: 'hide' });
  private selectedPoint;
  private allSurveyPoints: Multipoint = new Multipoint();
  private allSurveyPointsIndexes: any[] = [];
  protected _highlightedRow;
  private track_circle = new SimpleMarkerSymbol({
    color: undefined,
    size: 21,
    style: 'circle',
    outline: { color: 'red', width: 5 },
  });
  private selected_circle = new SimpleMarkerSymbol({
    color: undefined,
    size: 21,
    style: 'circle',
    outline: { color: 'cyan', width: 5 },
  });

  private readonly _breaklinesLayer = new GraphicsLayer({
    id: 'SurfaceDesignerVm.Breaklines',
    title: 'Breaklines',
  });
  private readonly _borderLayer = new GraphicsLayer({ id: 'SurfaceDesignerVm.Border', title: 'Border' });
  private _groundPointLabelsLayer: FeatureLayer | undefined;
  private readonly _groundPointsLayer = new GraphicsLayer({
    id: 'SurfaceDesignerVm.GroundPoints',
    title: 'Ground Points',
  });

  @observable drawingDesignElementTypeId: number = 0;
  @observable drawingDesignElementName: string = '';

  private _rebuildBorder = false;

  constructor(props: IPendingEditManagerVMConstructorProps) {
    super(props);
    // register layers to avoid removing from map

    if (!setStore.SETMapLayerKeepList.includes('SurfaceDesignerVm.Breaklines')) {
      setStore.SETMapLayerKeepList.push('SurfaceDesignerVm.Breaklines');
    }
    if (!setStore.SETMapLayerKeepList.includes('SurfaceDesignerVm.Border')) {
      setStore.SETMapLayerKeepList.push('SurfaceDesignerVm.Border');
    }
    if (!setStore.SETMapLayerKeepList.includes('SurfaceDesignerVm.GroundPoints')) {
      setStore.SETMapLayerKeepList.push('SurfaceDesignerVm.GroundPoints');
    }
    if (!setStore.SETMapLayerKeepList.includes('SurfaceDesignerVm.Measures')) {
      setStore.SETMapLayerKeepList.push('SurfaceDesignerVm.Measures');
    }
    if (!setStore.SETMapLayerKeepList.includes('SurfaceDesignerVm.Drawings')) {
      setStore.SETMapLayerKeepList.push('SurfaceDesignerVm.Drawings');
    }
    if (!setStore.SETMapLayerKeepList.includes('terrace.Drawings')) {
      setStore.SETMapLayerKeepList.push('terrace.Drawings');
    }
    if (!setStore.SETMapLayerKeepList.includes('alignment.Drawings')) {
      setStore.SETMapLayerKeepList.push('alignment.Drawings');
    }
    if (!setStore.SETMapLayerKeepList.includes('waterway.Drawings')) {
      setStore.SETMapLayerKeepList.push('waterway.Drawings');
    }
    if (!setStore.SETMapLayerKeepList.includes('pitRectangular.Drawings')) {
      setStore.SETMapLayerKeepList.push('pitRectangular.Drawings');
    }
    if (!setStore.SETMapLayerKeepList.includes('pitRound.Drawings')) {
      setStore.SETMapLayerKeepList.push('pitRound.Drawings');
    }
    if (!setStore.SETMapLayerKeepList.includes('pitDiked.Drawings')) {
      setStore.SETMapLayerKeepList.push('pitDiked.Drawings');
    }
    if (!setStore.SETMapLayerKeepList.includes('pad.Drawings')) {
      setStore.SETMapLayerKeepList.push('pad.Drawings');
    }
    if (!setStore.SETMapLayerKeepList.includes('landleveling.Drawings')) {
      setStore.SETMapLayerKeepList.push('landleveling.Drawings');
    }
    if (!setStore.SETMapLayerKeepList.includes('ugo.Drawings')) {
      setStore.SETMapLayerKeepList.push('ugo.Drawings');
    }
    if (!setStore.SETMapLayerKeepList.includes(AppConstants.OriginalContourLayerId)) {
      setStore.SETMapLayerKeepList.push(AppConstants.OriginalContourLayerId);
    }
    if (!setStore.SETMapLayerKeepList.includes(AppConstants.DesignedContourLayerId)) {
      setStore.SETMapLayerKeepList.push(AppConstants.DesignedContourLayerId);
    }

    // TODO: does this need to be here?
    gisModel.map = this.mapView.map;
    gisModel.view = this.mapView;

    // location widget to display coordinates
    gisModel.locationWidget = document.createElement('div');
    gisModel.locationWidget.id = 'coordsWidget';
    gisModel.locationWidget.className = 'esri-widget esri-component';
    gisModel.locationWidget.style.padding = '7px 15px 5px';
    if (gisModel.view.ui) gisModel.view.ui.add(gisModel.locationWidget, 'bottom-left');

    //intializing drawings for surface
    this.drawingsGroupLayer = this.drawingsGroupLayerByDesignElementType[
      appConfig.hardCodes.designElementTypeSuface.toString()
    ];
    this.drawings = this.drawingsByDesignElementType[appConfig.hardCodes.designElementTypeSuface.toString()];
    this.drawingTools = new DrawingToolsetViewModel(
      this.inputManager,
      this.drawingsByDesignElementType[appConfig.hardCodes.designElementTypeSuface.toString()]
    );

    this.drawingDesignElementTypeId =
      designStore.drawingTypeByDesignElementTypeId[appConfig.hardCodes.designElementTypeSuface].Id;
    this.drawingDesignElementName =
      designStore.drawingTypeByDesignElementTypeId[appConfig.hardCodes.designElementTypeSuface].name;

    //hook up the point translate related map clicks
    // this.setUpSelectPointOnMap(this.mapView);

    //kick off loading process of SurfaceDef
    // this.loadSurfaceDef();
  }

  @action.bound onMount(): void {
    // console.log('SurfaceDesignerVm.onMount');

    this.mapView.map.add(this._groundPointsLayer);
    if (this._groundPointLabelsLayer) this.mapView.map.add(this._groundPointLabelsLayer);
    this.mapView.map.add(this._borderLayer);
    this.mapView.map.add(this._breaklinesLayer);
  }
  @action.bound onUnmount(): void {
    // console.log('SurfaceDesignerVm.onUnmount');

    if (this.mapView && this.mapView.map) {
      this.mapView.map.remove(this._groundPointsLayer);
      if (this._groundPointLabelsLayer) this.mapView.map.remove(this._groundPointLabelsLayer);
      this.mapView.map.remove(this._borderLayer);
      this.mapView.map.remove(this._breaklinesLayer);

      const originalTinTrianglesLayer = this.mapView.map.findLayerById(
        AppConstants.OriginalTinTrianglesLayerId
      ) as GraphicsLayer;
      const designedTinTrianglesLayer = this.mapView.map.findLayerById(
        AppConstants.DesignedTinTrianglesLayerId
      ) as GraphicsLayer;
      if (designedTinTrianglesLayer) {
        designedTinTrianglesLayer.visible = stores.AppStore.designStore.sceneViewActive;
      } else {
        if (originalTinTrianglesLayer) {
          originalTinTrianglesLayer.visible = stores.AppStore.designStore.sceneViewActive;
        }
      }

      // const SurveyPointsLayer = this.mapView.map.findLayerById(AppConstants.SurveyPointsLayerId) as GraphicsLayer;
      // if (SurveyPointsLayer) {
      //   SurveyPointsLayer.visible = true;
      // }
    }
  }

  get surfaceDef(): SurfaceDef | undefined {
    this._loadSurfaceDefOnce();
    return this._surfaceDef;
  }
  set surfaceDef(value: SurfaceDef | undefined) {
    this._surfaceDef = value;
  }

  @action.bound setGroundPointsVisibility(value) {
    this._groundPointsLayer.visible = value;
    if (this._groundPointLabelsLayer) this._groundPointLabelsLayer.visible = value;
  }

  private _loadSurfaceDefPromise?: Promise<SurfaceDef | undefined>;
  @action.bound private async _loadSurfaceDefOnce(): Promise<SurfaceDef | undefined> {
    if (this._surfaceDef !== undefined) {
      if (this.mapView && this.mapView.map) {
        const OriginalTinTrianglesLayer = this.mapView.map.findLayerById(
          AppConstants.OriginalTinTrianglesLayerId
        ) as GraphicsLayer;
        if (OriginalTinTrianglesLayer) {
          OriginalTinTrianglesLayer.visible = false;
        }
        const SurveyPointsLayer = this.mapView.map.findLayerById(
          AppConstants.SurveyPointsLayerId
        ) as GraphicsLayer;
        if (SurveyPointsLayer) {
          SurveyPointsLayer.visible = false;
        }
      }
      return this._surfaceDef;
    }
    if (this._loadSurfaceDefPromise === undefined) {
      try {
        designStore.setLoader('Loading surface editor ...');

        this._loadSurfaceDefPromise = this._loadSurfaceDef();
        this._surfaceDef = await this._loadSurfaceDefPromise;
        this._loadSurfaceDefPromise = undefined;

        if (this._surfaceDef) {
          // redraw the border layer
          this._borderLayer.removeAll();
          const graphic = this._surfaceDef.border.generatePolygonGraphic(this.mapView.spatialReference);
          this._borderLayer.add(graphic);

          this._breaklinesLayer.removeAll();
          for (const breakline of this._surfaceDef.breaklinesSyncSet.getItems()) {
            const graphic = breakline.generateGraphic(this.mapView.spatialReference);
            this._breaklinesLayer.add(graphic);
          }

          this.drawSurveyPoints();
          this.drawSurveyPointFeatureLayer();

          this.addBreaklineTool = new AddBreaklineTool(
            this.mapView,
            this._surfaceDef,
            this.toolSlot,
            this._breaklinesLayer
          );
          this.deleteBreaklineTool = new DeleteBreaklineTool(
            this.mapView,
            this._surfaceDef,
            this.toolSlot,
            this._breaklinesLayer
          );
          this.addBorderTool = new InsertBorderPointsTool(
            this.mapView,
            this._surfaceDef,
            this.toolSlot,
            this._borderLayer
          );
          this.deleteBorderTool = new DeleteBorderPointTool(
            this.mapView,
            this._surfaceDef,
            this.toolSlot,
            this._borderLayer
          );
          this.editTinableTool = new EditTinablePointsTool(
            this.mapView,
            this._surfaceDef,
            this.toolSlot,
            this._groundPointsLayer
          );
          this.moveBreaklineTool = new MoveBreaklineTool(
            this.mapView,
            this._surfaceDef,
            this.toolSlot,
            this._breaklinesLayer
          );
          this.addPointBreaklineTool = new AddPointBreaklineTool(
            this.mapView,
            this._surfaceDef,
            this.toolSlot,
            this._breaklinesLayer
          );
          this.deletePointBreaklineTool = new DeletePointBreaklineTool(
            this.mapView,
            this._surfaceDef,
            this.toolSlot,
            this._breaklinesLayer
          );
          this.extendBreaklineTool = new ExtendBreaklineTool(
            this.mapView,
            this._surfaceDef,
            this.toolSlot,
            this._breaklinesLayer
          );
        } else {
          this.addBreaklineTool = undefined;
          this.deleteBreaklineTool = undefined;
          this.addBorderTool = undefined;
          this.deleteBorderTool = undefined;
          this.editTinableTool = undefined;
          this.moveBreaklineTool = undefined;
          this.addPointBreaklineTool = undefined;
          this.deletePointBreaklineTool = undefined;
          this.extendBreaklineTool = undefined;
        }
      } catch (error) {
        console.log(error);
      } finally {
        designStore.setLoader('');
      }

      await this._loadDrawings(this.drawingDesignElementName);

      this.snapAOI();

      if (this._rebuildBorder) {
        this.postBackendGeneratedBorder();
      }

      return this._surfaceDef;
    } else {
      return await this._loadSurfaceDefPromise;
    }
  }

  public drawSurveyPoints() {
    if (this._surfaceDef) {
      this._groundPointsLayer.removeAll();
      const groundPoints = this._surfaceDef.getAllGroundPointEditables(
        setStore.currentSurvey.surveyId,
        setStore.pointDescriptionFilter
      );
      for (const groundPoint of groundPoints) {
        const graphic = groundPoint.generateGraphic(this.mapView.spatialReference);
        this._groundPointsLayer.add(graphic);
      }
    }
  }

  public drawSurveyPointFeatureLayer() {
    if (this._groundPointLabelsLayer) {
      this.mapView.map?.remove(this._groundPointLabelsLayer);
      this._groundPointLabelsLayer.destroy();
      this._groundPointLabelsLayer = undefined;
    }
    if (this._surfaceDef) {
      const groundPoints = this._surfaceDef.getAllGroundPoints();
      setStore.allGroundPoints = groundPoints;
      const spatialReference = projectsLibraryStore.projectSpatialReference;
      const source = groundPoints.map((r, i) => ({
        geometry: new Point({
          spatialReference,
          hasZ: true,
          x: r.x,
          y: r.y,
          z: r.z,
        }),
        attributes: {
          ObjectID: i,
          name: r.name,
          y: r.y,
          x: r.x,
          z: r.z,
          description: r.description,
        },
      }));

      // create the points layer
      const symbol = new SimpleMarkerSymbol({ color: undefined, size: '0', outline: undefined });
      this._groundPointLabelsLayer = new FeatureLayer({
        source,
        fields: pointLabelFields,
        spatialReference,
        geometryType: 'point',
        renderer: new SimpleRenderer({ symbol }),
        id: 'SurfaceDesignerVm.GroundPointLabels',
        title: 'Ground Point Labels',
        objectIdField: 'ObjectID',
        labelingInfo: [pointLabelClass],
      });

      this.mapView.when(() => {
        if (this.mapView.map) {
          const index = this.mapView.map.layers.findIndex((l) => l.id === this._groundPointsLayer.id);
          this.mapView.map.add(this._groundPointLabelsLayer!, index + 1);
        }
      });
    }
  }

  private async _loadSurfaceDef(): Promise<SurfaceDef | undefined> {
    const currentProject = designStore.projectsStore.currentProject;
    if (currentProject === undefined) return undefined;
    if (currentProject.sessionList[0] === undefined) return undefined;
    const currentSessionUuid = currentProject.sessionList[0].sessionUuid;
    if (currentSessionUuid === undefined) return undefined;

    try {
      const surfaceDef = await SurfaceDef.fetchSurfaceDefWithBreaklinesAndUpdatedBorder(currentSessionUuid);
      setStore.surveyEditables = surfaceDef.surveyEditables;
      setStore.surfaceCentroid = surfaceDef.border.generatePolygonGeometry().centroid;
      return surfaceDef;
    } catch (error) {
      console.error('Unable to load SurfaceDef.');
      console.error(error);
      return undefined;
    }
  }

  private async _loadDrawings(drawingDesignElementName): Promise<void> {
    this.drawingsGroupLayer.removeAll();
    this.drawings.drawingSyncSet.resetSyncSet();
    const currentProject = designStore.projectsStore.currentProject;
    if (currentProject === undefined) return undefined;
    if (currentProject.sessionList[0] === undefined) return undefined;
    const currentSessionUuid = currentProject.sessionList[0].sessionUuid;
    if (currentSessionUuid === undefined) return undefined;

    try {
      designStore.setLoader('Loading drawings');

      const drawingsDesignElements = await DesignElementController.getAllDesignElementsBySessionAndTypeUsingGET(
        currentSessionUuid,
        drawingDesignElementName,
        true
      );

      if (drawingsDesignElements && drawingsDesignElements.data && 0 < drawingsDesignElements.data.length) {
        const drawingsJsonString = drawingsDesignElements.data[0].json!;
        const drawingsJson = JSON.parse(drawingsJsonString);
        designStore.designElementId = drawingsDesignElements.data[0].designElementId || 0;

        this.drawDesignElements(drawingsJson.drawings);
      }
    } catch (error) {
      console.log(error);
    } finally {
      designStore.setLoader('');
    }
  }

  private async snapAOI() {
    if (!this.mapView) return;

    const layers = [this._borderLayer as Layer, this._groundPointsLayer as Layer];

    const projectUuid = designStore.projectsStore.currentProjectId as string;
    if (projectUuid) {
      await this.mapView.when();
      const screenShot = await this.mapView?.takeScreenshot({ format: 'png', ignorePadding: true, layers });
      if (screenShot) {
        await saveScreenshot(projectUuid, screenShot.dataUrl, false, 0); // set to 0, to remove the api call for #4696
      }
    }
  }

  @action.bound startViewPointerMoveHandler() {
    this.allSurveyPointsIndexes = [];
    if (setStore.xyzPointsTable && setStore.xyzPointsTable.length > 0 && this.mapView && this.mapView.map) {
      const multipoint: Multipoint = new Multipoint({ spatialReference: this.mapView.spatialReference });
      for (let i = 0; i < setStore.xyzPointsTable.length; i++) {
        const row = setStore.xyzPointsTable[i];
        if (row.filtered) {
          const point = new Point({
            x: row.x,
            y: row.y,
            spatialReference: projectsLibraryStore.projectSpatialReference,
          });
          const projectedPoint = esriProjection.project(point, this.mapView.spatialReference) as Point;
          multipoint.addPoint(projectedPoint);

          this.allSurveyPointsIndexes.push({ point: projectedPoint, index: i });
        }
      }
      this.allSurveyPoints = multipoint;
      this.mapView.map.add(this.feedbackLayer);
      this.mapView.map.add(this.feedbackSelectedLayer);
      this.viewPointerMoveHandleOn = true;
      this.viewPointerMoveHandle = this.mapView.on('pointer-move', this.viewPointerMoveHandler);
    }
  }

  @action.bound removeViewPointerMoveHandler() {
    this.feedbackLayer?.removeAll();
    this.feedbackSelectedLayer?.removeAll();
    if (this.mapView && this.mapView.map) {
      if (this.feedbackLayer) this.mapView.map.remove(this.feedbackLayer);
      if (this.feedbackSelectedLayer) this.mapView.map.remove(this.feedbackSelectedLayer);
    }
    this.viewPointerMoveHandle?.remove();
    this.viewPointerDownHandle?.remove();
    this.viewPointerMoveHandle = undefined;
    this.viewPointerDownHandle = undefined;
    this.clearHighlight();
    this.selectedPoint = undefined;
    this.viewPointerMoveHandleOn = false;
  }

  @action.bound private async viewPointerMoveHandler(e: __esri.ViewPointerMoveEvent) {
    this.feedbackLayer.graphics?.removeAll();
    if (this.viewPointerDownHandle) {
      this.viewPointerDownHandle.remove();
      this.viewPointerDownHandle = undefined;
    }
    if (setStore.xyzPointsTable) {
      const point = this.mapView.toMap(e);
      const findResult = esriGeometryEngine.nearestVertex(this.allSurveyPoints, point);

      if (findResult.distance < 11) {
        const resultPoint = new Point({
          x: findResult.coordinate.x,
          y: findResult.coordinate.y,
          spatialReference: this.mapView.spatialReference,
        });
        const nextPointGraphic = new Graphic({
          geometry: resultPoint,
          symbol: this.track_circle,
        });
        this.feedbackLayer.add(nextPointGraphic);
        this.viewPointerDownHandle = this.mapView.on('pointer-down', () =>
          this.viewPointerDownHandler(resultPoint)
        );
      }
    }
  }

  @action.bound private viewPointerDownHandler(point) {
    this.feedbackSelectedLayer.removeAll();
    this.clearHighlight();
    if (this.selectedPoint && this.selectedPoint.x === point.x && this.selectedPoint.y === point.y) {
      this.selectedPoint = undefined;
    } else {
      this.selectedPoint = point;
      const selectedPointGraphic = new Graphic({
        geometry: point,
        symbol: this.selected_circle,
      });
      this.feedbackSelectedLayer.add(selectedPointGraphic);

      if (this.allSurveyPointsIndexes.length > 0) {
        const object = this.allSurveyPointsIndexes.find((obj) => {
          return obj.point.x === point.x && obj.point.y === point.y;
        });
        if (object?.index && object.index >= 0) {
          this.highlightRow(object.index);
        }
      }
    }
  }

  private highlightRow(index: number) {
    const id = `key-xyzSurveyPoint-${index}`;
    this._highlightedRow = id;
    const row = document.getElementById(this._highlightedRow);
    row?.classList.add('focused-row');
    row?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }

  private clearHighlight() {
    const row = document.getElementById(this._highlightedRow);
    row?.classList.remove('focused-row');
    this._highlightedRow = undefined;
  }

  @action.bound async openToolBar() {
    //Before open ToolBar, collapse layerListWidget/basemapGalleryWidget if they are open
    designStore?.layerListWidget?.collapse();
    designStore?.basemapGalleryWidget?.collapse();

    // await super.openToolBar();
    await this.inputManager.cancelCurrentOperationAsync();
    this.hideToolbar = false;
  }

  @action.bound async onSaveStructurePendingEdits(e) {
    e.preventDefault();
    try {
      designStore.setLoader('Saving Pending Edits');

      // Waterway Design edits
      if (this.wdtGroupsLength > 0) {
        await this.saveReachEdits();
        await waterwayDesignStore.defaultLiningMaterialChange();
        waterwayDesignStore.waterwayDesignObject.DisableSaveButtonDesignPage = true;
      }

      // Terrace Design edits
      if (designStore.terraceGroupsLength > 0) {
        const isSuccess = await saveTerraceReachEdits();
        if (isSuccess) {
          await terraceDesignStore.handleUiStateUpdate({ uiStateType: TerraceUiState.ChannelDesignSave.string });
        }
        terraceDesignStore.constructedProfileStations = [];
        terraceDesignStore.waterLevelData = [];
        terraceDesignStore.settledProfileStations = [];
      }

      // UGO Design edits
      if (designStore.ugoGroupsLength > 0) {
        await saveUgoReachEdits();
        //const isSuccess = await saveUgoReachEdits();
        // if (isSuccess) {
        //   await ugoDesignStore.handleUiStateUpdate({ uiStateType: UGOUiState.ChannelDesignSave.string });
        // }
        // ugoDesignStore.constructedProfileStations = [];
        // ugoDesignStore.waterLevelData = [];
        // ugoDesignStore.settledProfileStations = [];
      }

      //Logic for save Pending edit/add structure drawings
      const isSuccess = await this.saveStructureSurfaceElments();
      if (isSuccess) {
        // update sync data status
        this.drawings.clearSyncStatus();
      }

      if (isSuccess && gisModel.view) {
        try {
          await DesignContainerVM.loadOriginalSurface();

          if (this.surfaceDef) this.surfaceDef = undefined;
          setStore.resetTabs();
          MessageCenter.clearMessages();
        } catch (error) {
          MessageCenter.addError((error as Error).message);
        } finally {
          // setStore.isLoading = false;
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      designStore.setLoader('');
    }
  }

  private async saveStructureSurfaceElments() {
    // setStore.isLoading = true;
    let isSuccess = false;

    const drawingsJson: any[] = this.extractDrawingJson();

    const currentProject = designStore.projectsStore.currentProject;
    if (currentProject === undefined) return undefined;
    if (currentProject.sessionList[0] === undefined) return undefined;
    const currentSessionUuid = currentProject.sessionList[0].sessionUuid;
    if (currentSessionUuid === undefined) return undefined;

    const postJSON: any = {
      designElementId: designStore.designElementId,
      designElementName: this.drawingDesignElementName,
      designElementTypeId: this.drawingDesignElementTypeId,
      session_UUID: currentSessionUuid,
    };

    if (drawingsJson) postJSON.drawingDesign = { drawings: drawingsJson };
    if (
      (this.drawings.drawingSyncSet.getPendingEdits() &&
        this.drawings.drawingSyncSet.getPendingEditsCount() > 0) ||
      (drawingsJson && drawingsJson.length > 0)
    ) {
      try {
        const response: any = await StructureDrawingsController.updateDrawingsUsingPOST(postJSON);
        if (response && response.data) {
          isSuccess = response.data.processStatus === 'Success';
          designStore.designElementId = response.data.designElementId;
        }
        MessageCenter.clearMessages();
      } catch (error) {
        MessageCenter.addError((error as Error).message);
      } finally {
        // setStore.isLoading = false;
      }
    }

    return isSuccess;
  }

  @action.bound async onSavePendingEdits(e) {
    e.preventDefault();

    // Reset survey point table
    if (setStore.xyzPointsTable && setStore.xyzPointsTable.length > 0) {
      setStore.pointDescriptionFilter = 'All';
      for (const point of setStore.xyzPointsTable) {
        point.filtered = true;
      }
    }

    // Turn off "continuous" Tools
    this.deleteBorderTool?.deactivate();
    this.editTinableTool?.deactivate();

    try {
      designStore.setLoader('Saving Pending Edits');
      //Logic for save Pending edit/add breaklines, boarder points, drawings

      const isSuccess = await this.saveSurfaceElments();
      if (isSuccess) {
        // update sync data status
        this.surfaceDef?.clearGroundPointsSyncStatus();
        this.surfaceDef?.clearBorderSyncStatus();
        this.surfaceDef?.clearBreaklinesSyncStatus();
        this.drawings.clearSyncStatus();
      }

      // this.surfaceDef?.isBorderModified  should be reset
      // this.surfaceDef?.breaklinesPendingEditCount should be reset
      // this.uncommittedEdits = 0; // temporary reset for testing warning notification
      if (gisModel.view) {
        // update the surface elevation layer
        // setStore.isLoading = true;
        try {
          await DesignContainerVM.loadOriginalSurface();
          // await DesignContainerVM.updateSurfaceTinElevationLayer();
          // await DesignContainerVM.instance.openOrGenerateSurface(true);

          if (this.surfaceDef) this.surfaceDef = undefined;
          await SurfaceDef.fetchSurfaceDefWithBreaklinesAndUpdatedBorder(
            designStore.projectsStore.currentProject.sessionList[0].sessionUuid
          );
          setStore.maxId = 0;

          // Handle edit all surveys
          if (setStore.currentSurvey.surveyId === 'all-surveys') {
            const allpoints: GroundPoint[] = [];
            for (const survey of setStore.surveyEditables as XyzSurvey[]) {
              allpoints.push(...survey.groundPoints);
            }
            setStore.xyzPointsTable = allpoints;
            setStore.initialXyzPointsTable = allpoints;
          }

          // Handle edit single survey
          else {
            setStore.xyzPointsTable = setStore.surveyEditables.find(
              (s) => s.surveyId === setStore.currentSurvey.surveyId
            )?.groundPoints;
            setStore.initialXyzPointsTable = setStore.surveyEditables.find(
              (s) => s.surveyId === setStore.currentSurvey.surveyId
            )?.groundPoints;
          }

          if (setStore.xyzPointsTable) {
            setStore.initXYZPointsLength = setStore.xyzPointsTable.length;
            setStore.maxId = setStore.xyzPointsTable.length;
          }

          // setStore.resetTabs();
          MessageCenter.clearMessages();
        } catch (error) {
          MessageCenter.addError((error as Error).message);
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      if (this.viewPointerMoveHandleOn) {
        this.removeViewPointerMoveHandler();
        this.startViewPointerMoveHandler();
      }
      designStore.setLoader('');
    }
  }

  @action.bound onUndoLastEdit() {
    this.disableUndo = true;
    // Undo last edit for Terrace
    if (designStore.designElementTypeId === appConfig.hardCodes.designElementTypeTerrace) {
      // get cloned object from before latest edit
      const originalObject = JSON.parse(
        JSON.stringify(
          terraceDesignStore.terraceGroupsOriginals[terraceDesignStore.terraceGroupsOriginals.length - 2]
            .currentObject
        )
      );
      // get number of groups for latest edit
      const editGroupCount =
        terraceDesignStore.terraceGroupsOriginals[terraceDesignStore.terraceGroupsOriginals.length - 1]
          .groupCount;
      // reset terraceDesignObject based on second-to-last saved object
      Object.assign(terraceDesignStore.terraceDesignObject, originalObject);
      // use groupCount to remove that count of groups from the terraceGroups array
      designStore.terraceGroups.splice(designStore.terraceGroups.length - editGroupCount);
      // remove the last two items from terraceGroupsOriginals
      terraceDesignStore.terraceGroupsOriginals.splice(-1);
      if (terraceDesignStore.terraceGroupsOriginals.length === 0) {
        terraceDesignStore.terraceGroupsOriginals.push({
          groupCount: 0,
          currentObject: JSON.parse(JSON.stringify(terraceDesignStore.terraceDesignObject)),
        });
      }

      this.terraceGroupsLength = terraceDesignStore.terraceGroupsOriginals.length - 1;
      designStore.terraceGroupsLength = this.terraceGroupsLength;
      if (designStore.terraceGroupsLength === 0 && terraceDesignStore.hasCalculatedData) {
        designStore.setTerraceSecLayerVisibility = true;
      }
      designStore.toggleResetValues = true;
    }
    // Undo last edit for Waterway
    else if (designStore.designElementTypeId === appConfig.hardCodes.designElementTypeWaterway) {
      // get cloned object from before latest edit
      const originalObject = JSON.parse(
        JSON.stringify(
          waterwayDesignStore.wdtGroupsOriginals[waterwayDesignStore.wdtGroupsOriginals.length - 2].currentObject
        )
      );
      // get number of groups for latest edit
      const editGroupCount =
        waterwayDesignStore.wdtGroupsOriginals[waterwayDesignStore.wdtGroupsOriginals.length - 1].groupCount;
      // reset waterwayDesignObject based on second-to-last saved object
      Object.assign(waterwayDesignStore.waterwayDesignObject, originalObject);
      // use groupCount to remove that count of groups from the wdtGroups array
      this.wdtGroups.splice(this.wdtGroups.length - editGroupCount);
      // remove the last two items from wdtGroupsOriginals
      waterwayDesignStore.wdtGroupsOriginals.splice(-1);
      if (waterwayDesignStore.wdtGroupsOriginals.length === 0) {
        waterwayDesignStore.wdtGroupsOriginals.push({
          groupCount: 0,
          currentObject: JSON.parse(JSON.stringify(waterwayDesignStore.waterwayDesignObject)),
        });
      }

      this.wdtGroupsLength = waterwayDesignStore.wdtGroupsOriginals.length - 1;
      designStore.wdtGroupsLength = this.wdtGroupsLength;
      if (this.wdtGroupsLength === 0)
        waterwayDesignStore.waterwayDesignObject.DisableSaveButtonDesignPage = true;
      designStore.toggleResetValues = true;
    }
    // Undo last edit for UGO
    else if (designStore.designElementTypeId === appConfig.hardCodes.designElementTypeUGO) {
      // get cloned object from before latest edit
      const originalObject = JSON.parse(
        JSON.stringify(
          ugoDesignStore.ugoGroupsOriginals[ugoDesignStore.ugoGroupsOriginals.length - 2].currentObject
        )
      );
      // get number of groups for latest edit
      const editGroupCount =
        ugoDesignStore.ugoGroupsOriginals[ugoDesignStore.ugoGroupsOriginals.length - 1].groupCount;
      // reset ugoDesignObject based on second-to-last saved object
      Object.assign(ugoDesignStore.ugoDesignObject, originalObject);
      // use groupCount to remove that count of groups from the ugoGroups array
      designStore.ugoGroups.splice(designStore.ugoGroups.length - editGroupCount);
      // remove the last two items from ugoGroupsOriginals
      ugoDesignStore.ugoGroupsOriginals.splice(-1);
      if (ugoDesignStore.ugoGroupsOriginals.length === 0) {
        ugoDesignStore.ugoGroupsOriginals.push({
          groupCount: 0,
          currentObject: JSON.parse(JSON.stringify(ugoDesignStore.ugoDesignObject)),
        });
      }

      this.ugoGroupsLength = ugoDesignStore.ugoGroupsOriginals.length - 1;
      designStore.ugoGroupsLength = this.ugoGroupsLength;
      if (designStore.ugoGroupsLength === 0 && ugoDesignStore.hasCalculatedData) {
        designStore.setUgoSecLayerVisibility = true;
      }
      designStore.toggleResetValues = true;
    }
    // Undo last edit for Breaklines
    else if (designStore.designElementTypeId === appConfig.hardCodes.designElementTypeSuface) {
      const lastOp = this.surfaceDef?.breaklinesSyncSet.getLastOperation();

      if (lastOp === 'insert') {
        this.surfaceDef?.breaklinesSyncSet.deleteLastItem();
      } else if (lastOp === 'update' || lastOp === 'delete') {
        this.surfaceDef?.breaklinesSyncSet.undoLastEdit();
      }

      this.addBreaklineTool?.redraw();
      designStore.breakLineGroupsLength = this.surfaceDef!.breaklinesPendingEditCount;
    }
  }

  @action.bound async onClearAllEdits() {
    this.disableUndo = true;
    if (designStore.designElementTypeId === appConfig.hardCodes.designElementTypeTerrace) {
      designStore.terraceGroups = [];
      this.terraceGroupsLength = 0;
      designStore.terraceGroupsLength = this.terraceGroupsLength;
      const originalObject = JSON.parse(
        JSON.stringify(terraceDesignStore.terraceGroupsOriginals[0].currentObject)
      );
      Object.assign(terraceDesignStore.terraceDesignObject, originalObject);
      terraceDesignStore.terraceGroupsOriginals.splice(1);
      designStore.toggleResetValues = true;
      if (terraceDesignStore.hasCalculatedData) {
        designStore.setTerraceSecLayerVisibility = true;
      }
    } else if (designStore.designElementTypeId === appConfig.hardCodes.designElementTypeWaterway) {
      this.wdtGroups = [];
      this.wdtGroupsLength = 0;
      designStore.wdtGroupsLength = this.wdtGroupsLength;
      const originalObject = JSON.parse(JSON.stringify(waterwayDesignStore.wdtGroupsOriginals[0].currentObject));
      Object.assign(waterwayDesignStore.waterwayDesignObject, originalObject);
      waterwayDesignStore.wdtGroupsOriginals.splice(1);
      designStore.toggleResetValues = true;
      waterwayDesignStore.waterwayDesignObject.DisableSaveButtonDesignPage = true;
    } else if (designStore.designElementTypeId === appConfig.hardCodes.designElementTypeUGO) {
      designStore.ugoGroups = [];
      this.ugoGroupsLength = 0;
      designStore.ugoGroupsLength = this.ugoGroupsLength;
      const originalObject = JSON.parse(JSON.stringify(ugoDesignStore.ugoGroupsOriginals[0].currentObject));
      Object.assign(ugoDesignStore.ugoDesignObject, originalObject);
      ugoDesignStore.ugoGroupsOriginals.splice(1);
      designStore.toggleResetValues = true;
      if (ugoDesignStore.hasCalculatedData) {
        designStore.setUgoSecLayerVisibility = true;
      }
    } else if (designStore.designElementTypeId === appConfig.hardCodes.designElementTypeSuface) {
      await this.resetToSavedBreaklines();
      designStore.breakLineGroupsLength = 0;
    }
  }

  private async resetToSavedBreaklines() {
    this._breaklinesLayer.removeAll();
    const syncSet = this.surfaceDef?.breaklinesSyncSet;
    if (syncSet) {
      for (let i = syncSet.__syncObjects.length - 1; i >= 0; i--) {
        const syncObject = syncSet.__syncObjects[i];
        syncObject.clearPendingEditCount();
        if (syncObject.syncStatus === 'pending-insert' && syncObject.current) {
        // Delete new breaklines
          syncSet.deleteItem(syncObject.current);
        } else if ((syncObject.syncStatus === 'pending-delete' || syncObject.syncStatus === 'pending-update') && syncObject.original) {
          // Revert edited breaklines back to original state
          syncObject.update(syncObject.original);
        }
      }

      for (const breakline of syncSet.getItems()) {
        const graphic = breakline.generateGraphic(this.mapView.spatialReference);
        this._breaklinesLayer.add(graphic);
      }
    }
  }


  private async saveSurfaceElments() {
    // setStore.isLoading = true;
    let isSuccess = false;

    const pointsJson = this.extractModifiedPointsJson();
    const borderJson = this.extractBorderJson();
    const breaklinesJson: any[] = this.extractBreaklineJson();
    const drawingsJson: any[] = this.extractDrawingJson();

    // console.log('Surface_def_id: ' + designStore.currentSurface.surfaceDefId);

    const postJSON: any = {
      surfaceDefId: designStore.currentSurface.surfaceDefId,
    };

    if (pointsJson) postJSON.points = pointsJson;
    if (drawingsJson) postJSON.drawingDesign = { drawings: drawingsJson };
    if (breaklinesJson) postJSON.breaklineData = { featureTypeId: 2, breaklines: breaklinesJson };
    if (borderJson) postJSON.border = { points: borderJson };

    postJSON.borderRebuild = this.surfaceDef ? this.surfaceDef.borderRebuild : false;
    postJSON.contourInterval = designStore.contourInterval;

    if (
      (pointsJson && pointsJson.length > 0) ||
      this.drawings.drawingSyncSet.getPendingEdits() ||
      drawingsJson.length > 0 ||
      breaklinesJson.length > 0 ||
      borderJson.length > 0
    ) {
      try {
        this._rebuildBorder = this._surfaceDef ? this._surfaceDef.borderRebuild : false;
        const response: any = await SurfaceController.editSurfaceUsingPOST(postJSON);
        isSuccess = response.data.status === 'success';
        MessageCenter.clearMessages();
      } catch (error) {
        MessageCenter.addError((error as Error).message);
      } finally {
        // setStore.isLoading = false;
      }
    }

    return isSuccess;
  }

  private async postBackendGeneratedBorder() {
    // setStore.isLoading = true;
    let isSuccess = false;

    this._surfaceDef?.applyBorderOnSurveyEditables(
      this._surfaceDef.border.generatePolygonGeometry(),
      this._surfaceDef.border.generatePolylineGeometry()
    );

    const pointsJson = this._surfaceDef?.pointsModifiedByEditing.map((p) => {
      return {
        id: p.id,
        pointName: p.name,
        x: p.x,
        y: p.y,
        z: p.z,
        editing_status: p.editing_status,
        visible: p.visible ? 1 : 0,
        tinable: p.tinable ? 1 : 0,
        featureDescription: p.description,
      };
    });

    // console.log('Surface_def_id: ' + designStore.currentSurface.surfaceDefId);

    const postJSON: any = {
      surfaceDefId: designStore.currentSurface.surfaceDefId,
    };

    if (pointsJson) postJSON.points = pointsJson;

    postJSON.borderRebuild = false;

    if (pointsJson && pointsJson!.length > 0) {
      try {
        this._rebuildBorder = false;
        const response: any = await SurfaceController.editSurfaceUsingPOST(postJSON);
        isSuccess = response.data.status === 'success';
        MessageCenter.clearMessages();
      } catch (error) {
        MessageCenter.addError((error as Error).message);
      } finally {
        // setStore.isLoading = false;
      }
    }

    if (isSuccess) {
      // update sync data status
      this.surfaceDef?.clearGroundPointsSyncStatus();
    }

    return isSuccess;
  }

  private extractBorderJson() {
    if (this.surfaceDef?.isBorderModified) {
      return this.surfaceDef?.border.borderPoints.map((p) => {
        return {
          name: p.name,
          x: p.x,
          y: p.y,
          z: p.z,
          description: p.description,
        };
      });
    } else {
      return [];
    }
  }

  private extractModifiedPointsJson() {
    return this.surfaceDef?.pointsModifiedByEditing.map((p) => {
      return {
        id: p.id,
        pointName: p.name,
        x: p.x,
        y: p.y,
        z: p.z,
        editing_status: p.editing_status,
        visible: p.visible ? 1 : 0,
        tinable: p.tinable ? 1 : 0,
        featureDescription: p.description,
      };
    });
  }

  private extractBreaklineJson() {
    const breaklinesJson: any[] = [];

    const breaklinePendingEdits = [
      this.surfaceDef?.breaklinesSyncSet.getPendingInserts(),
      this.surfaceDef?.breaklinesSyncSet.getPendingUpdates(),
      this.surfaceDef?.breaklinesSyncSet.getPendingDeletes(),
    ];
    const editings = ['add', 'update', 'delete'];
    for (const edits of breaklinePendingEdits) {
      const editStatus = editings.shift();
      if (edits) {
        for (const syncObject of edits!) {
          const line =
            syncObject.syncStatus === 'pending-delete' ? syncObject.deletedObject : syncObject.current;

          if (line) {
            breaklinesJson.push({
              editing_status: editStatus,
              breaklineId: line.breaklineId,
              breaklineType: 'Standard',
              featureName: 'breakline',
              featureDescription: line.eft_type,
              points: line.groundPoints.map((p) => {
                return { name: p.name, description: p.description, x: p.x, y: p.y, z: p.z };
              }),
            });
          }
        }
      }
    }
    return breaklinesJson;
  }


  // user leaving surface section -->
  @observable surveyDesignForceOpen: boolean = false;

  @action.bound setUncommittedEdits() {
    this.uncommittedEdits =
      (this.surfaceDef?.isBorderModified ? 1 : 0) +
    (this.surfaceDef?.breaklinesPendingEditCount ? this.surfaceDef?.breaklinesPendingEditCount : 0) +
      (this.surfaceDef ? this.surfaceDef!.countPointsModified : 0) +
      (this.surfaceDef?.contourIntervalModified ? 1 : 0) +
      (this.drawings ? this.drawings.pendingEditCount : 0);
    return this.uncommittedEdits;
  }

  public inChangeDesignElement: boolean = false;

  @action.bound async setDesignElementToolbar(designElementType: string) {
    if (this.inChangeDesignElement) {
      return;
    }
    this.inChangeDesignElement = true;

    designStore.hideSingleCrossSection = true;
    designStore.hideCrossSectionToolbar = true;
    this.closeToolBar(parseInt(designElementType), designStore.currentSurface.surfaceDefClassName);
    this.measuresGroupLayer.removeAll();
    if (!this.mapView.map.findLayerById(this.measuresGroupLayer.id)) {
      this.mapView.map.add(this.measuresGroupLayer);
    }

    for (const key of Object.keys(this.drawingsGroupLayerByDesignElementType)) {
      if (key === designElementType) {
        this.drawingsGroupLayer = this.drawingsGroupLayerByDesignElementType[key];
        this.drawings = this.drawingsByDesignElementType[key];
        this.drawingTools = new DrawingToolsetViewModel(
          this.inputManager,
          this.drawingsByDesignElementType[key]
        );
        if (!this.mapView.map.findLayerById(this.drawingsGroupLayer.id)) {
          this.mapView.map.add(this.drawingsGroupLayerByDesignElementType[key]);
        }

        //to handle save structure drawings
        designStore.designElementId = 0;
        if (designStore.drawingTypeByDesignElementTypeId[designElementType]) {
          this.drawingDesignElementTypeId = designStore.drawingTypeByDesignElementTypeId[designElementType].Id;
          this.drawingDesignElementName = designStore.drawingTypeByDesignElementTypeId[designElementType].name;
        }

        await this._loadDrawings(this.drawingDesignElementName);
      } else {
        if (this.mapView.map) this.mapView.map.remove(this.drawingsGroupLayerByDesignElementType[key]);
      }
    }
    this.inChangeDesignElement = false;
  }

  @observable notification: any = {
    openSurvey: '',
    uncommittedEdits: '',
  };

  @action.bound async getErrors() {
    this.notification.openSurvey = await MessageCenter.addErrorLabelFromCode('Survey_20'); // 'Please Save or Cancel Survey Edits before Leaving Survey Design.';
    this.notification.uncommittedEdits = await MessageCenter.addErrorLabelFromCode('Survey_21'); // 'Please Save or Cancel Pending Edits before Leaving Surface Design.';
  }

  @action.bound async userLeavingWarningOnOk(e: any) {
    if (setStore.currentSurvey.surveyName) {
      setStore.currentTab = setStore.currentSurvey.surveyId ? 'Edit Survey' : 'New Survey';
      this.surveyDesignForceOpen = true;
    }
    if (this.uncommittedEdits) this.hideToolbar = false;
    return false;
  }
  // <-- user leaving surface section

  // WDT Reaches --->
  @action.bound async addWDTEdits(
    editedWaterwayVertexMap: Map<string, WaterwayVertexParamDTO>,
    manualEdits: boolean = false,
    lastEdit: boolean = false
  ) {
    this.wdtGroups.push({
      groupID: this.wdtGroups.length + 1,
      waterWayVertextParamDTOMap: editedWaterwayVertexMap,
    });

    waterwayDesignStore.waterwayDesignObject.disableCalculateButtonDesignPage = false;
    waterwayDesignStore.waterwayDesignObject.DisableSaveButtonDesignPage = false;
    waterwayDesignStore.waterwayDesignObject.hasWaterwayCalculations = false;
    designStore.displayResultBanner = false;
    if (!manualEdits) await this.retriveFlowCalculationResults(waterwayDesignStore.waterwayDesignObject);
    if (!lastEdit) {
      this.wdtGroupCount += 1;
    } else {
      this.wdtGroupCount += 1;
      waterwayDesignStore.wdtGroupsOriginals.push({
        groupCount: this.wdtGroupCount,
        currentObject: JSON.parse(JSON.stringify(waterwayDesignStore.waterwayDesignObject)),
      });
      this.wdtGroupCount = 0;
      this.disableUndo = false;
    }
    this.wdtGroupsLength = waterwayDesignStore.wdtGroupsOriginals.length - 1;
    designStore.wdtGroupsLength = this.wdtGroupsLength;
    if (!designStore.hideSingleCrossSection && this.wdtGroupsLength > 0) {
      if (designStore.structureLayerExists === true) {
        designStore.structureLayerExists = false;
        await MessageCenter.addErrorFromCode('CS_3');
      }
    }
  }

  @action.bound async retriveFlowCalculationResults(currentObj) {
    const mappedArr = this.createObjWithMappedProperties(currentObj.waterwayDesignStations);
    const filteredMappedArr = mappedArr.filter((item) => item.reachId !== 'START' && item.reachId !== 'END');

    //Renamed the properties in filteredMappedArr

    const renamedFilteredMappedArr = filteredMappedArr.map((item) => ({
      addedQ: item.addedQ,
      channelElev: item.channelZ,
      reachId: item.reachId,
      runoffQ: item.runoffQ,
      stationDistance: item.stationLength,
      totalQ: item.totalQ,
    }));
    const reqObj = {
      alignedElementDesignParametersId: waterwayDesignStore.alignedDesignElementId,
      reachParams: renamedFilteredMappedArr,
    };
    try {
      const response = await WaterwayDesignToolsController.redistributeRunoffUsingPOST(reqObj);
      if (response && response.data) {
        const responseArr = response.data.reachParams;

        //Update waterwayDesignStations here

        if (waterwayDesignStore.waterwayDesignObject.waterwayDesignStations.length > 0 && responseArr) {
          for (let i = 0; i < filteredMappedArr.length; i++) {
            // const objToUpdate = waterwayDesignStore.waterwayDesignObject.waterwayDesignStations.find((item) => item.reachId == responseArr[i].reachId)!;
            if (
              waterwayDesignStore.waterwayDesignObject.waterwayDesignStations[i + 1].reachId === 'START' ||
              waterwayDesignStore.waterwayDesignObject.waterwayDesignStations[i + 1].reachId === 'END'
            )
              return;
            waterwayDesignStore.waterwayDesignObject.waterwayDesignStations[i + 1].channelZ = responseArr[
              i
            ].channelElev!;
            waterwayDesignStore.waterwayDesignObject.waterwayDesignStations[i + 1].addedQ = responseArr[
              i
            ].addedQ!;
            waterwayDesignStore.waterwayDesignObject.waterwayDesignStations[i + 1].runoffQ = +responseArr[
              i
            ].runoffQ!.toFixed(1);
            waterwayDesignStore.waterwayDesignObject.waterwayDesignStations[i + 1].stationLength = responseArr[
              i
            ].stationDistance!;
            waterwayDesignStore.waterwayDesignObject.waterwayDesignStations[i + 1].totalQ = +responseArr[
              i
            ].totalQ!.toFixed(1);
          }
        }
      }
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    }
  }

  //function to return only required properties from an object
  @action.bound createObjWithMappedProperties(inputObj) {
    return inputObj.map((station) => {
      const mappedObj = {};
      ['addedQ', 'channelZ', 'reachId', 'runoffQ', 'stationLength', 'totalQ'].forEach((prop) => {
        if (prop == 'reachId') {
          mappedObj[prop] = station[prop];
        } else {
          mappedObj[prop] = +station[prop];
        }
      });
      return mappedObj;
    });
  }

  @action.bound async saveReachEdits() {
    // save waterway design edits via Pending Edit Manager
    let isSuccess = false;
    const payload: SaveReachEditsRequestDTO = {
      alignedElementDesignParametersId: waterwayDesignStore.alignedDesignElementId,
      reachGroupList: this.wdtGroups.map((group) => {
        return {
          groupId: group.groupID,
          waterWayVertextParamDTOMap: Object.fromEntries(group.waterWayVertextParamDTOMap),
        };
      }),
    };
    try {
      const response = await WaterwayDesignToolsReachJsonController.saveReachEditsUsingPOST(payload);
      if (response && response.data) {
        isSuccess = response.data === 'SUCCESS';
        if (isSuccess) {
          this.clearWDTReachesSyncStatus();
        }
      }
    } catch (error) {
      MessageCenter.addError((error as Error).message);
    } finally {
      setStore.isLoading = false;
    }
    return isSuccess;
  }

  @action.bound clearWDTReachesSyncStatus() {
    this.wdtGroups = [];
    this.wdtGroupsLength = 0;
    designStore.wdtGroupsLength = this.wdtGroupsLength;
    // reset WDT original data tracker
    waterwayDesignStore.wdtGroupsOriginals = [];
    waterwayDesignStore.wdtGroupsOriginals.push({
      groupCount: 0,
      currentObject: JSON.parse(JSON.stringify(waterwayDesignStore.waterwayDesignObject)),
    });
  }
  // <--- WDT Reaches

  // Terrace Reaches  --->

  @action.bound async addTerraceEdits(
    editedTerraceVertexMap: Map<string, TerraceVertexParamsDTO>,
    manualEdits: boolean = false,
    lastEdit: boolean = false
  ) {
    designStore.terraceGroups.push({
      groupID: designStore.terraceGroups.length + 1,
      terraceVertexParamsDTOMap: editedTerraceVertexMap,
    });
    terraceDesignStore.displayResultBanner = false;

    if (!lastEdit) {
      this.terraceGroupCount += 1;
    } else {
      this.terraceGroupCount += 1;
      terraceDesignStore.terraceGroupsOriginals.push({
        groupCount: this.terraceGroupCount,
        currentObject: JSON.parse(JSON.stringify(terraceDesignStore.terraceDesignObject)),
      });
      this.terraceGroupCount = 0;
      this.disableUndo = false;
    }
    this.terraceGroupsLength = terraceDesignStore.terraceGroupsOriginals.length - 1;
    designStore.terraceGroupsLength = this.terraceGroupsLength;
    if (!designStore.hideSingleCrossSection && designStore.terraceGroupsLength > 0) {
      if (designStore.structureLayerExists === true) {
        designStore.structureLayerExists = false;
        await MessageCenter.addErrorFromCode('CS_3');
      }
    }
    designStore.setTerraceSecLayerVisibility = false;
  }

  // <--- Terrace Reaches

  // UGO Reaches  --->

  @action.bound async addUgoEdits(
    editedUgoVertexMap: Map<string, UgoVertexParamsDTO>,
    lastEdit: boolean = false
  ) {
    designStore.ugoGroups.push({
      groupID: designStore.ugoGroups.length + 1,
      ugoVertexParamsDTOMap: editedUgoVertexMap,
    });
    ugoDesignStore.displayResultBanner = false;

    if (!lastEdit) {
      this.ugoGroupCount += 1;
    } else {
      this.ugoGroupCount += 1;
      ugoDesignStore.ugoGroupsOriginals.push({
        groupCount: this.ugoGroupCount,
        currentObject: JSON.parse(JSON.stringify(ugoDesignStore.ugoDesignObject)),
      });
      this.ugoGroupCount = 0;
      this.disableUndo = false;
    }
    this.ugoGroupsLength = ugoDesignStore.ugoGroupsOriginals.length - 1;
    designStore.ugoGroupsLength = this.ugoGroupsLength;
    if (!designStore.hideSingleCrossSection && designStore.ugoGroupsLength > 0) {
      if (designStore.structureLayerExists === true) {
        designStore.structureLayerExists = false;
        await MessageCenter.addErrorFromCode('CS_3');
      }
    }
    designStore.setTerraceSecLayerVisibility = false;
  }

  // <--- UGO Reaches

  @action.bound editSurveyContourInterval(value: number) {
    designStore.contourInterval = value;
    this.surfaceDef!.contourIntervalModified = true;
  }

  @action.bound setUniqueDescriptions() {
    if (setStore.allGroundPoints && setStore.allGroundPoints.length > 0) {
      // Count occurrences of each description
      const descriptionCounts = setStore.allGroundPoints.reduce((acc, item) => {
        acc[item.description] = (acc[item.description] || 0) + 1;
        return acc;
      }, {});

      // Filter descriptions that appear more than once
      const filteredDescriptions = Object.keys(descriptionCounts).filter((desc) => descriptionCounts[desc] > 1);

      // Sort descriptions
      this.uniqueDescriptions = filteredDescriptions.sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
      );
    }
  }

  @action.bound addBreaklineEdits() {
    //PEM for Autobreak and Manual breakline
    const lastOp = this.surfaceDef?.breaklinesSyncSet.getLastOperation();
    if (lastOp === 'none') {
      this.disableUndo = true;
    } else {
      this.disableUndo = false;
    }
    designStore.breakLineGroupsLength = this.surfaceDef!.breaklinesPendingEditCount;
  }

  @action.bound async toggleToolbar(designElementTypeId: number, surfaceType) {
    await this.inputManager.cancelCurrentOperationAsync();
    if (this.activeTab === 'Breaklines') {
      this.setUniqueDescriptions();
    }
    this.hideToolbar = !this.hideToolbar;

    if (this.hideToolbar) {
      if (this.toolSlot.current) {
        await this.toolSlot.current?.deactivate();
      }
      if (designElementTypeId !== 2 || surfaceType === 'dem') {
        this.activeTab = 'Measure';
        this.measureToolType = undefined;
        this.measureResults = new MeasureResultsModel(this.measuresGroupLayer);
      } else {
        this.activeTab = 'Breaklines';
      }
    }
  }

  @action.bound async onClickBreaklinesTool(e: any) {
    e.preventDefault();
    await this.inputManager.cancelCurrentOperationAsync();
    if (this.toolSlot.current) {
      await this.toolSlot.current?.deactivate();
    }
    this.activeTab = 'Breaklines';
    this.setUniqueDescriptions();
  }

  @action.bound selectDescription(value) {
    this.selctedPointDescription = value;
  }

  @action.bound toggleBreaklineClosed(e: React.ChangeEvent<HTMLInputElement>) {
    this.isClosed = e.target.checked;
  }

  @action.bound async onClickBorderpointsTool(e: any) {
    e.preventDefault();
    await this.inputManager.cancelCurrentOperationAsync();
    if (this.toolSlot.current) {
      await this.toolSlot.current?.deactivate();
    }
    this.activeTab = 'Borderpoints';
  }

  @action.bound async onClickTinableTool(e: any) {
    e.preventDefault();
    await this.inputManager.cancelCurrentOperationAsync();
    if (this.toolSlot.current) {
      await this.toolSlot.current?.deactivate();
    }
    this.activeTab = 'Tinable';
  }

  @action.bound onClickGenerate = () => {
    const messages = this.addBreaklineTool?.generateBreaklinesByDescription(
      this.selctedPointDescription,
      this.isClosed
    );
    MessageCenter.clearMessages();
    if (messages?.length) {
      const [type, code] = messages[0].split(':');
      if (type === 'Warning') {
        MessageCenter.addWarningFromCode(code);
      } else if (type === 'Error') {
        MessageCenter.addErrorFromCode(code);
      }
    }
  }
}

export const saveTerraceReachEdits = async () => {
  //save terrace design edits via Pending Edit Manager
  let isSuccess = false;

  const payload: SaveTerraceReachEditsDTO = {
    alignedElementDesignParametersId: terraceDesignStore.terraceDesignObject.alignedElementDesignParametersId,
    terraceReachGroupList: designStore.terraceGroups.map(group => {
      return {
        groupId: group.groupID,
        terraceVertexParamsDTOMap: Object.fromEntries(group.terraceVertexParamsDTOMap)
      }
    })
  }
  try {
    const response = await TerraceReachController.saveTerraceReachEditsUsingPOST(payload);
    if (response && response.data) {
      const responseData: SaveTerraceReachEditsResponseDTO = response.data
      if (responseData.errorResponse && responseData.errorResponse.errorType === 'ERROR' && responseData.errorResponse.errorCode && responseData.errorResponse.errorMessage) {
        MessageCenter.addNotification(responseData.errorResponse.errorMessage, responseData.errorResponse.errorType, responseData.errorResponse.errorCode);
      }
      else if (!responseData.errorResponse || (responseData.errorResponse && responseData.errorResponse.errorType !== 'ERROR' && responseData.errorResponse.errorCode && responseData.errorResponse.errorMessage)) {
        isSuccess = true;
        //show if responseData has one or more information messages
        if ((responseData.errorResponse && responseData.errorResponse.errorType === 'INFORMATION' && responseData.errorResponse.errorCode && responseData.errorResponse.errorMessage)) {
          MessageCenter.addNotification(responseData.errorResponse.errorMessage, responseData.errorResponse.errorType, responseData.errorResponse.errorCode);
        }
        //show if responseData has one or more warning messages
        if ((responseData.errorResponse && responseData.errorResponse.errorType === 'WARNING' && responseData.errorResponse.errorCode && responseData.errorResponse.errorMessage)) {
          MessageCenter.addNotification(responseData.errorResponse.errorMessage, responseData.errorResponse.errorType, responseData.errorResponse.errorCode);
        }
        clearTerraceReachesSyncStatus();
        terraceDesignStore.constructedProfileStations = [];
        terraceDesignStore.waterLevelData = [];
        if (designStore.designElementTypeId === appConfig.hardCodes.designElementTypeTerrace) {
          terraceDesignStore.terraceInputsChanged = false;
        }
        terraceDesignStore.settledProfileStations = [];
        terraceDesignStore.closeMatchModal = !terraceDesignStore.closeMatchModal;
      }
    }

  } catch (error) {
    MessageCenter.addError((error as Error).message);
  } finally {
    setStore.isLoading = false;
  }
  return isSuccess;
}

const clearTerraceReachesSyncStatus = () => {
  designStore.terraceGroups = [];
  designStore.terraceGroupsLength = 0
  terraceDesignStore.hasCalculatedData = false;
  //designStore.terraceGroupsLength = terraceGroupsLength;
  // reset Terrace original data tracker
  terraceDesignStore.terraceGroupsOriginals = [];
  terraceDesignStore.terraceGroupsOriginals.push(
    {
      groupCount: 0,
      currentObject: JSON.parse(JSON.stringify(terraceDesignStore.terraceDesignObject))
    }
  );
}

export const clearAllTerraceEdits = async () => {
  if (designStore.designElementTypeId === appConfig.hardCodes.designElementTypeTerrace) {
    designStore.terraceGroups = [];
    designStore.terraceGroupsLength = 0;
    if (terraceDesignStore.terraceGroupsOriginals && terraceDesignStore.terraceGroupsOriginals.length > 0) {
      const firstOriginal = terraceDesignStore.terraceGroupsOriginals[0];

      if (firstOriginal && firstOriginal.currentObject) {
        const originalObject = JSON.parse(JSON.stringify(firstOriginal.currentObject));
        Object.assign(terraceDesignStore.terraceDesignObject, originalObject);
      }
    }
    terraceDesignStore.terraceGroupsOriginals.splice(1);
    if (terraceDesignStore.hasCalculatedData) {
      designStore.setTerraceSecLayerVisibility = true;
    }
  }
}

export const clearAllUgoEdits = async () => {
  if (designStore.designElementTypeId === appConfig.hardCodes.designElementTypeUGO) {
    designStore.ugoGroups = [];
    designStore.ugoGroupsLength = 0;
    if (ugoDesignStore.ugoGroupsOriginals && ugoDesignStore.ugoGroupsOriginals.length > 0) {
      const firstOriginal = ugoDesignStore.ugoGroupsOriginals[0];

      if (firstOriginal && firstOriginal.currentObject) {
        const originalObject = JSON.parse(JSON.stringify(firstOriginal.currentObject));
        Object.assign(ugoDesignStore.ugoDesignObject, originalObject);
      }
    }
    ugoDesignStore.ugoGroupsOriginals.splice(1);
    if (ugoDesignStore.hasCalculatedData) {
      designStore.setUgoSecLayerVisibility = true;
    }
  }
}

export const saveUgoReachEdits = async () => {
  //save ugo design edits via Pending Edit Manager
  let isSuccess = false;

  const payload: SaveUgoReachEditsRequestDTO = {
    alignedElementDesignParametersId: Number(ugoDesignStore.ugoDesignObject.alignedElementDesignParamsId),
    reachGroupList: designStore.ugoGroups.map(group => {
      return {
        groupId: group.groupID,
        ugoVertexParamsDTOMap: Object.fromEntries(group.ugoVertexParamsDTOMap)
      }
    })
  }
  try {
    const response = await UgoReachController.saveUgoReachEditsUsingPOST(payload);
    if (response && response.data) {
      //const responseData: SaveUgoReachEditsResponseDTO = response.data
      /*
      const responseData: SaveUgoReachEditsResponseDTO = response.data
      if (responseData.errorResponse && responseData.errorResponse.errorType === 'ERROR' && responseData.errorResponse.errorCode && responseData.errorResponse.errorMessage) {
        MessageCenter.addNotification(responseData.errorResponse.errorMessage, responseData.errorResponse.errorType, responseData.errorResponse.errorCode);
      }
      else if (!responseData.errorResponse || (responseData.errorResponse && responseData.errorResponse.errorType !== 'ERROR' && responseData.errorResponse.errorCode && responseData.errorResponse.errorMessage)) {
        isSuccess = true;
        //show if responseData has one or more information messages
        if ((responseData.errorResponse && responseData.errorResponse.errorType === 'INFORMATION' && responseData.errorResponse.errorCode && responseData.errorResponse.errorMessage)) {
          MessageCenter.addNotification(responseData.errorResponse.errorMessage, responseData.errorResponse.errorType, responseData.errorResponse.errorCode);
        }
        //show if responseData has one or more warning messages
        if ((responseData.errorResponse && responseData.errorResponse.errorType === 'WARNING' && responseData.errorResponse.errorCode && responseData.errorResponse.errorMessage)) {
          MessageCenter.addNotification(responseData.errorResponse.errorMessage, responseData.errorResponse.errorType, responseData.errorResponse.errorCode);
        }
        clearUgoReachesSyncStatus();
        //ugoDesignStore.constructedProfileStations = [];
        // ugoDesignStore.waterLevelData = [];
        if (designStore.designElementTypeId === appConfig.hardCodes.designElementTypeUGO) {
          ugoDesignStore.ugoInputsChanged = false;
        }
        //  ugoDesignStore.settledProfileStations = [];
        //  ugoDesignStore.closeMatchModal = !ugoDesignStore.closeMatchModal;
      }
      */
      isSuccess = true;
      clearUgoReachesSyncStatus();
    }

  } catch (error) {
    MessageCenter.addError((error as Error).message);
  } finally {
    setStore.isLoading = false;
  }
  return isSuccess;
}

const clearUgoReachesSyncStatus = () => {
  designStore.ugoGroups = [];
  designStore.ugoGroupsLength = 0
  ugoDesignStore.hasCalculatedData = false;
  // reset Ugo original data tracker
  ugoDesignStore.ugoGroupsOriginals = [];
  ugoDesignStore.ugoGroupsOriginals.push(
    {
      groupCount: 0,
      currentObject: JSON.parse(JSON.stringify(ugoDesignStore.ugoDesignObject))
    }
  );
}